import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable } from "@fortawesome/free-solid-svg-icons";
import { Form, Button } from "react-bootstrap";
import "./styles/OESettingsComponent.css";
import CheckboxWithTooltip from "./CheckboxWithTooltip";
import MaxDiffConfigComponent from "./MaxDiffConfigComponent";
import { FormattableInput } from "./FormattableInput";
import QuotaConfigComponent from "./QuotaConfigComponent";
import { buildQuotaQuestion, parseQuotaQuestion } from "./quotaUtils";

const OESettingsComponent = ({
  oeParameters,
  label,
  setOeParameters,
  qtype,
  savePropertyChanges,
  selectedVariable,
  onQuotaUpsert,
  onQuotaRemove,
  existingQuotaQuestion,
  availableQuotas = [],
  onAssignQuota,
  onOpenQuotaModal,
}) => {
  console.log("OE selectedVariable:", selectedVariable);
  console.log("OE rows:", selectedVariable?.rows);
  const [showMaxDiffModal, setShowMaxDiffModal] = React.useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [maxDiffDesign, setMaxDiffDesign] = useState({
    method: "create",
    itemsPerSet: 5,
    tasksPerRespondent: 10,
    designVersions: 3,
    designData: "",
    designType: "balanced"
  });
  const [showQuotaSelectPopup, setShowQuotaSelectPopup] = useState(false);

  useEffect(() => {
    if (oeParameters?.isRequired === undefined) {
      const newParams = { ...(oeParameters || {}), isRequired: true };
      savePropertyChanges(newParams, "oeParameters");
    }
  }, [selectedVariable?.Question_number]);

  // Handler for all changes - directly update and save
  const handleChange = (property, value) => {
    const newParams = {
      ...oeParameters,
      [property]: value
    };

    // Handle exclusive checkboxes
    const exclusiveRankingOptions = ['isDropDown', 'isClick', 'isNumberRank'];

    if (exclusiveRankingOptions.includes(property) && value === true) {
      // Uncheck other ranking options
      exclusiveRankingOptions.forEach(opt => {
        if (opt !== property) {
          newParams[opt] = false;
        }
      });
    }

    // Handle other exclusivity rules
    if (property === 'isDrag' && value === true) {
      newParams.isSlider = false;
    } else if (property === 'isSlider' && value === true) {
      newParams.isDrag = false;
    }

    savePropertyChanges(newParams, "oeParameters");
  };

  // Special handler for MaxDiff
  const handleMaxDiffChange = (e) => {
    const isChecked = e.target.checked;
    handleChange('isMaxDiff', isChecked);
    if (isChecked) {
      setShowMaxDiffModal(true);
    }
  };

  // Handler for MaxDiff configuration updates
  const handleMaxDiffConfigUpdate = (config) => {
    const newParams = {
      ...oeParameters,
      ...config
    };
    setOeParameters(newParams);
    savePropertyChanges(newParams, "oeParameters");
  };

  // Special handler for Quota: flag the source question and open the config
  // modal. Unchecking deletes the generated hidden quota question.
  // OESettingsComponent.jsx

  const handleQuotaSave = (config) => {
    const newParams = {
      ...oeParameters,
      quotaConfig: config.cells  // [{ index, count }]
    };
    savePropertyChanges(newParams, "oeParameters");
  };

  // Helper — assigned quota list se compute karo
  const getAssignedList = () => {
    const assignedRaw = selectedVariable?.assignedQuota || "";
    if (Array.isArray(assignedRaw)) return assignedRaw;
    return assignedRaw.split(",").map(s => s.trim()).filter(Boolean);
  };

  // Popup close karte waqt — agar koi quota assigned nahi toh isQuota false karo
  const closePopupSafely = () => {
    const assignedList = getAssignedList();
    if (assignedList.length === 0) {
      const newParams = { ...oeParameters, isQuota: false, quotaConfig: [] };
      savePropertyChanges(newParams, "oeParameters");
    }
    setShowQuotaSelectPopup(false);
  };

  const handleQuotaCheckboxChange = () => {
    if (oeParameters?.isQuota) {
      // Already true tha — uncheck kiya, sab clear karo
      const newParams = { ...oeParameters, isQuota: false, quotaConfig: [] };
      savePropertyChanges(newParams, "oeParameters");
      onAssignQuota?.("");
    } else {
      // False tha — check kiya, sirf popup kholo, isQuota abhi true mat karo
      setShowQuotaSelectPopup(true);
    }
  };

  const handleDoneClick = () => {
    const assignedList = getAssignedList();
    if (assignedList.length > 0) {
      // Quota selected hai — isQuota true karo
      handleChange('isQuota', true);
    } else {
      // Koi quota select nahi kiya — checkbox uncheck rakho
      const newParams = { ...oeParameters, isQuota: false, quotaConfig: [] };
      savePropertyChanges(newParams, "oeParameters");
    }
    setShowQuotaSelectPopup(false);
  };

  const handleRemoveAll = () => {
    const newParams = { ...oeParameters, isQuota: false, quotaConfig: [] };
    savePropertyChanges(newParams, "oeParameters");
    onAssignQuota?.("");
    setShowQuotaSelectPopup(false);
  };

  const toggleQuotaSelection = (quotaName) => {
    const assignedList = getAssignedList();
    const isSelected = assignedList.includes(quotaName);
    const newList = isSelected
      ? assignedList.filter(n => n !== quotaName)
      : [...assignedList, quotaName];
    onAssignQuota?.(newList.join(","));
  };

  return (
    <div className="oe-settings-section">
      {/* Header */}
      <div className="oe-header">
        <h4>{label}</h4>
        {/* {JSON.stringify(selectedVariable)}; */}
        {/* print(selectedVariable); */}
        {selectedVariable && (
          <div className="oe-variable-info">
            <small className="text-muted">
              Source: {selectedVariable.Question_number || selectedVariable.name}
              {selectedVariable.type && ` (${selectedVariable.type})`}
            </small>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="oe-main-content">
        {/* Settings Box */}
        <div className="oe-settings-box">
          <h5>Settings</h5>

          {/* Checkboxes Row */}
          <div className="oe-input-checkboxes">
            <CheckboxWithTooltip
              checked={oeParameters?.isRequired === true}
              onChange={(e) => handleChange('isRequired', e.target.checked)}
              label="Required"
              tooltip="Make this field mandatory - respondents must provide an answer"
            />
            <CheckboxWithTooltip
              checked={oeParameters?.isDecimal || false}
              onChange={(e) => handleChange('isDecimal', e.target.checked)}
              label="Decimal"
              tooltip="It will change the format from number to decimals"
            />

            <CheckboxWithTooltip
              checked={oeParameters?.isSumTotal || false}
              onChange={(e) => handleChange('isSumTotal', e.target.checked)}
              label="Sum Total"
              tooltip="Display and validate the total sum of all numeric inputs"
            />

            <CheckboxWithTooltip
              checked={oeParameters?.isSum100 || false}
              onChange={(e) => handleChange('isSum100', e.target.checked)}
              label="Sum 100"
              tooltip="Ensure all numeric inputs add up to exactly 100"
            />
          </div>

          {/* Input Fields */}
          <div className="oe-input-grid">
            <Form.Group>
              <Form.Label className="oe-form-label">Size</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.size || "10"}
                onChange={(e) => handleChange('size', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Input size"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Input Type</Form.Label>
              <Form.Select
                size="sm"
                value={oeParameters?.inputType || "text"}
                onChange={(e) => handleChange('inputType', e.target.value)}
                className="oe-form-control-sm"
              >
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="tel">Phone</option>
                <option value="url">URL</option>
                <option value="date">Date</option>
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Pre-text</Form.Label>
              <FormattableInput
                value={oeParameters?.preText || ""}
                onChange={(e) => handleChange('preText', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Text before input"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Post-text</Form.Label>
              <FormattableInput
                value={oeParameters?.postText || ""}
                onChange={(e) => handleChange('postText', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Text after input"
              />
            </Form.Group>
          </div>

          <div className="oe-numeric-grid">
            <Form.Group>
              <Form.Label className="oe-form-label">
                Termination Condition
              </Form.Label>
              <FormattableInput
                value={oeParameters?.term || ""}
                onChange={(e) => handleChange('term', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Enter condition"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Exact Selection</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.exact || ""}
                onChange={(e) => handleChange('exact', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Exact Selection"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Minimum Value</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.min ?? ""}
                onChange={(e) => handleChange('min', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Min"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Maximum Value</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.max || ""}
                onChange={(e) => handleChange('max', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Max"
                className="oe-form-control-sm"
              />
            </Form.Group>

            {oeParameters?.isDate && (
              <Form.Group>
                <CheckboxWithTooltip
                  checked={oeParameters?.isDateRange || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const newParams = { ...oeParameters, isDateRange: checked };
                    if (!checked) {
                      newParams.min_date = "";
                      newParams.max_date = "";
                    }
                    savePropertyChanges(newParams, "oeParameters");
                  }}
                  label="Set Date Range"
                  tooltip="Restrict selectable dates to a Min/Max range"
                />
              </Form.Group>
            )}

            {oeParameters?.isDate && oeParameters?.isDateRange && (
              <>
                <Form.Group>
                  <Form.Label className="oe-form-label">Min Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={oeParameters?.min_date || ""}
                    onChange={(e) => handleChange('min_date', e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className="oe-form-control-sm"
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label className="oe-form-label">Max Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={oeParameters?.max_date || ""}
                    onChange={(e) => handleChange('max_date', e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className="oe-form-control-sm"
                  />
                </Form.Group>
              </>
            )}

            <Form.Group>
              <Form.Label className="oe-form-label">Total Sum min Value</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.summin || ""}
                onChange={(e) => handleChange('summin', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Total Sum Min"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Total Sum Max Value</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.summax || ""}
                onChange={(e) => handleChange('summax', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Total Sum Max"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Digits in box</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.maxdigit || ""}
                onChange={(e) => handleChange('maxdigit', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="No of digits in box"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Table Corner Text</Form.Label>
              <FormattableInput
                value={oeParameters?.tbCorner || ""}
                onChange={(e) => handleChange('tbCorner', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Text to show in table corner"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Prefill</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.prefill || ""}
                onChange={(e) => handleChange('prefill', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Prefill value"
                className="oe-form-control-sm"
              />
            </Form.Group>
            <Form.Group className="oe-form-label-margin-bottom">
              <Form.Label className="oe-form-label">Number of Boxes</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.nboxes || ""}
                onChange={(e) => handleChange('nboxes', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Number of OE boxes"
                className="oe-form-control-sm"
              />
            </Form.Group>

          </div>
        </div>

        {/* General Settings and Validate - Side by Side */}
        <div className="oe-two-column-grid">
          {/* General Settings */}
          <div className="oe-settings-box oe-general-settings">
            <h5>General Settings</h5>
            {oeParameters?.nboxes > 0 && (
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">Compulsory no of boxes</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.nboxesrequired || ""}
                  onChange={(e) => handleChange('nboxesrequired', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Number of OE boxes to be filled compulsory"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {/* Checkboxes in two columns */}
            <div className="oe-checkbox-grid">
              <div className="oe-checkbox-column">
                <CheckboxWithTooltip
                  checked={oeParameters?.isHidden || false}
                  onChange={(e) => handleChange('isHidden', e.target.checked)}
                  label="Hidden Question"
                  tooltip="Make a Question Hidden"
                />

                {/* Quota Checkbox + Edit button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckboxWithTooltip
                    checked={oeParameters?.isQuota || false}
                    onChange={handleQuotaCheckboxChange}
                    label="Quota Question"
                    tooltip="Make Question as Quota"
                  />
                  {oeParameters?.isQuota && (
                    <button
                      onClick={() => setShowQuotaSelectPopup(true)}
                      style={{
                        background: 'none',
                        border: '1px solid #c4b5fd',
                        color: '#7c3aed',
                        borderRadius: 5,
                        padding: '2px 8px',
                        fontSize: 11,
                        cursor: 'pointer',
                        lineHeight: 1.4,
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <CheckboxWithTooltip
                  checked={oeParameters?.isSamePage || false}
                  onChange={(e) => handleChange('isSamePage', e.target.checked)}
                  label="Display on Same Page"
                  tooltip="Keep this question on the same page as the immediately preceding question. To group 3+ questions together, enable this on each consecutive question in the group."
                />

                <CheckboxWithTooltip
                  checked={oeParameters?.isNoanswer || false}
                  onChange={(e) => handleChange('isNoanswer', e.target.checked)}
                  label="No Answer for Decipher"
                  tooltip="This will append No Answer row for Decipher programming"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isRightLeft || false}
                  onChange={(e) => handleChange('isRightLeft', e.target.checked)}
                  label="Right Left Setup"
                  tooltip="This will make Right Left Combination of rows and columns along with scale provided in Columns reverse statement in Statements. Only applicable in Grids"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isCarousel || false}
                  onChange={(e) => handleChange('isCarousel', e.target.checked)}
                  label="Carousel Setup"
                  tooltip="This will make Carousel Setup"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isCol || false}
                  onChange={(e) => handleChange('isCol', e.target.checked)}
                  label="Column Layout"
                  tooltip="Make grid as Column selection"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isSinCol || false}
                  onChange={(e) => handleChange('isSinCol', e.target.checked)}
                  label="Show Rows as Columns"
                  tooltip="Show single response as columns"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isMaxDiff || false}
                  onChange={handleMaxDiffChange}
                  label="Max Diff Setup"
                  tooltip="Enable MaxDiff with balanced design or custom design import"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isWithoutMaxDiff || false}
                  onChange={(e) => handleChange('isWithoutMaxDiff', e.target.checked)}
                  label="Max Diff without Setup"
                  tooltip="Enable MaxDiff without excel setup"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isSlider || false}
                  onChange={(e) => handleChange('isSlider', e.target.checked)}
                  label="Slider"
                  tooltip="Display input as a slider control for numeric values"
                />

              </div>

              <div className="oe-checkbox-column">

                {/* Mutually exclusive ranking options */}
                <div className="mutually-exclusive-group">
                  <CheckboxWithTooltip
                    checked={oeParameters?.isDrag || false}
                    onChange={(e) => handleChange('isDrag', e.target.checked)}
                    label="Drag"
                    tooltip="Enable drag functionality for reordering items"
                  />

                  <CheckboxWithTooltip
                    checked={oeParameters?.isDragDrop || false}
                    onChange={(e) => handleChange('isDragDrop', e.target.checked)}
                    label="Drag and Drop"
                    tooltip="Enable drag and drop functionality for reordering items"
                  />

                  <CheckboxWithTooltip
                    checked={oeParameters?.isDropDown || false}
                    onChange={(e) => handleChange('isDropDown', e.target.checked)}
                    label="Drop Down"
                    tooltip="Enable Drop down functionality for answers"
                  />
                  <CheckboxWithTooltip
                    checked={oeParameters?.isClick || false}
                    onChange={(e) => handleChange('isClick', e.target.checked)}
                    label="Click to Rank"
                    tooltip="Enable Clickable design for Rank Functionality in Confirmit"
                  />
                  <CheckboxWithTooltip
                    checked={oeParameters?.isNumberRank || false}
                    onChange={(e) => handleChange('isNumberRank', e.target.checked)}
                    label="Number to Rank"
                    tooltip="Enable Number design for Rank Functionality"
                  />
                </div>

                <CheckboxWithTooltip
                  checked={oeParameters?.isTextarea || false}
                  onChange={(e) => handleChange('isTextarea', e.target.checked)}
                  label="Textarea"
                  tooltip="Use a multi-line text area instead of single-line text input"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isDate || false}
                  onChange={(e) => handleChange('isDate', e.target.checked)}
                  label="Date Picker"
                  tooltip="Display a calendar date picker for date selection"
                />
                {oeParameters?.isDate && (
                  <div className="oe-datepicker-suboptions" style={{ marginLeft: 20 }}>
                    <CheckboxWithTooltip
                      checked={oeParameters?.past || false}
                      onChange={(e) => handleChange('past', e.target.checked)}
                      label="Past"
                      tooltip="Allow selecting past dates"
                    />
                    <CheckboxWithTooltip
                      checked={oeParameters?.present || false}
                      onChange={(e) => handleChange('present', e.target.checked)}
                      label="Present"
                      tooltip="Allow selecting the present date"
                    />
                    <CheckboxWithTooltip
                      checked={oeParameters?.future || false}
                      onChange={(e) => handleChange('future', e.target.checked)}
                      label="Future"
                      tooltip="Allow selecting future dates"
                    />
                  </div>
                )}
                <CheckboxWithTooltip
                  checked={oeParameters?.isPageBreak || false}
                  onChange={(e) => handleChange('isPageBreak', e.target.checked)}
                  label="Remove pagebreak after question"
                  tooltip="Remove suspend tag"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.istablelook || false}
                  onChange={(e) => handleChange('istablelook', e.target.checked)}
                  label="Show Numeric Question as Table"
                  tooltip="This will show the numeric question in table format"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.optional || false}
                  onChange={(e) => handleChange('optional', e.target.checked)}
                  label="Optional"
                  tooltip="This will make question non manadatory"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.mainFolder || false}
                  onChange={(e) => handleChange('mainFolder', e.target.checked)}
                  label="Main Folder Started"
                  tooltip="This will place all question after this in main folder in Confirmit"
                />
              </div>
            </div>

            {oeParameters?.isMaxDiff && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowMaxDiffModal(true)}
                className="mt-2"
              >
                <FontAwesomeIcon icon={faTable} className="me-1" />
                Configure MaxDiff Design
              </Button>
            )}

            {oeParameters?.isMaxDiff && oeParameters?.maxDiffDesign && (
              <div className="maxdiff-summary mt-2 p-2 bg-light rounded">
                <small>
                  <strong>MaxDiff Design:</strong><br />
                  Method: {oeParameters.maxDiffDesign.designMethod}<br />
                  Versions: {oeParameters.maxDiffDesign.actualVersions || oeParameters.maxDiffDesign.designVersions}<br />
                  Tasks per version: {oeParameters.maxDiffDesign.actualTasksPerVersion || oeParameters.maxDiffDesign.tasksPerRespondent}<br />
                  Items per set: {oeParameters.maxDiffDesign.actualItemsPerSet || oeParameters.maxDiffDesign.itemsPerSet}<br />
                  {oeParameters.maxDiffDesign.uniqueItemsUsed && (
                    <>Unique items: {oeParameters.maxDiffDesign.uniqueItemsUsed}<br /></>
                  )}
                  {oeParameters.maxDiffDesign.designMethod === 'pasted' && (
                    <span className="text-success">✓ Auto-detected from paste</span>
                  )}
                </small>
              </div>
            )}

            <MaxDiffConfigComponent
              oeParameters={oeParameters}
              setOeParameters={setOeParameters}
              onConfigUpdate={handleMaxDiffConfigUpdate}
              handleChange={handleChange}
              showMaxDiffModal={showMaxDiffModal}
              setShowMaxDiffModal={setShowMaxDiffModal}
              maxDiffDesign={maxDiffDesign}
              setMaxDiffDesign={setMaxDiffDesign}
            />

            {/* Assigned quota summary */}
            {oeParameters?.isQuota && getAssignedList().length > 0 && (
              <div style={{
                marginTop: 8,
                padding: '6px 10px',
                background: '#f0f0ff',
                borderRadius: 6,
                border: '1px solid #c4b5fd',
                fontSize: 12,
                color: '#4338ca',
              }}>
                <strong>Assigned:</strong> {getAssignedList().join(", ")}
              </div>
            )}

            {oeParameters?.isQuota && existingQuotaQuestion?.rows?.length > 0 && (
              <div className="quota-summary mt-2 p-2 bg-light rounded">
                <small>
                  <strong>Quota ({existingQuotaQuestion.rows.length}):</strong><br />
                  {existingQuotaQuestion.rows.map((r) => (
                    <span key={r.index}>
                      {r.text}: {r.punchLogic} → {r.quotaCounts}<br />
                    </span>
                  ))}
                </small>
              </div>
            )}

            {/* Quota Select Popup */}
            {showQuotaSelectPopup && (
              <div className="quota-select-overlay" onClick={closePopupSafely}>
                <div className="quota-select-modal" onClick={(e) => e.stopPropagation()}>
                  {/* Header */}
                  <div className="quota-select-header">
                    <h5 className="quota-select-title">Select Quota(s) for this Question</h5>
                    <button onClick={closePopupSafely} className="quota-select-close-btn">×</button>
                  </div>

                  {availableQuotas.length === 0 ? (
                    <div className="quota-select-empty">
                      <p className="quota-select-warning-text">⚠ No quotas configured yet.</p>
                      {onOpenQuotaModal && (
                        <button
                          onClick={() => { setShowQuotaSelectPopup(false); onOpenQuotaModal(); }}
                          className="quota-select-create-first-btn"
                        >
                          + Create Quota First
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="quota-select-hint">
                        Choose which quota(s) apply to this question (multiple allowed):
                      </p>

                      <div className="quota-select-list">
                        {availableQuotas.map((q) => {
                          const assignedList = getAssignedList();
                          const isSelected = assignedList.includes(q.quotaName);

                          return (
                            <div
                              key={q.quotaName}
                              onClick={() => toggleQuotaSelection(q.quotaName)}
                              className={`quota-select-item ${isSelected ? "selected" : ""}`}
                            >
                              <div className="quota-select-item-content">
                                <div className="quota-select-item-header">
                                  <div className={`quota-select-checkbox ${isSelected ? "checked" : ""}`}>
                                    {isSelected && <span className="quota-select-checkbox-icon">✓</span>}
                                  </div>
                                  <span className="quota-select-item-name">{q.quotaName}</span>
                                </div>
                                {q.rows?.length > 0 && (
                                  <div className="quota-select-item-preview">
                                    {q.rows.slice(0, 3).map(r => r.name).join(' · ')}
                                    {q.rows.length > 3 && ` · +${q.rows.length - 3} more`}
                                  </div>
                                )}
                              </div>
                              <span className="quota-select-item-badge">
                                {q.rows?.length || 0} rows
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Create new quota */}
                      <div
                        className="quota-select-create-new"
                        onClick={() => { setShowQuotaSelectPopup(false); onOpenQuotaModal?.(); }}
                      >
                        <span className="quota-select-create-new-text">+ Create New Quota</span>
                      </div>

                      {/* Footer */}
                      <div className="quota-select-footer">
                        {getAssignedList().length > 0 ? (
                          <button onClick={handleRemoveAll} className="quota-select-remove-all-btn">
                            ✕ Remove All
                          </button>
                        ) : (
                          <div />
                        )}

                        <button onClick={handleDoneClick} className="quota-select-done-btn">
                          Done
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Validate */}
          <div className="oe-settings-box">
            <h5>Validate</h5>
            <Form.Group className="oe-form-label-margin-bottom">
              <Form.Label className="oe-form-label">Validation logic</Form.Label>
              <FormattableInput
                value={oeParameters?.validationLogic || ""}
                onChange={(e) => handleChange('validationLogic', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Validation logic"
              />
              <Form.Label className="oe-form-label">Error Message</Form.Label>
              <FormattableInput
                value={oeParameters?.errorMessage || ""}
                onChange={(e) => handleChange('errorMessage', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Error Message if any"
              />
            </Form.Group>

            <div className="oe-validate-checkboxes">
              <CheckboxWithTooltip
                checked={oeParameters?.isSequential || false}
                onChange={(e) => handleChange('isSequential', e.target.checked)}
                label="Sequential"
                tooltip="Ensure responses are entered in sequential order"
              />
              <CheckboxWithTooltip
                checked={oeParameters?.isFirstComp || false}
                onChange={(e) => handleChange('isFirstComp', e.target.checked)}
                label="First Box Compulsory"
                tooltip="Ensure First Box is compulsory in Open end"
              />
              <CheckboxWithTooltip
                checked={oeParameters?.isDuplicate || false}
                onChange={(e) => handleChange('isDuplicate', e.target.checked)}
                label="Duplicate Entries Check"
                tooltip="Ensure to check the duplicate entries in Open end"
              />
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">Disable cells</Form.Label>
                <FormattableInput
                  value={oeParameters?.disabledCells || ""}
                  onChange={(e) => handleChange('disabledCells', e.target.value)}
                  type="contentEditable"
                  className="oe-formattable-input"
                  placeholder="Disable Cells: R1C1, R2C3 R1 C4 etc"
                />
              </Form.Group>

            </div>
          </div>
        </div>
      </div>
      {/* {JSON.stringify(selectedVariable)} */}
    </div>
  );
};

export default OESettingsComponent;